/**
 * from-product — the single adapter that turns a loose product object (the
 * varied shapes used across sidebar widgets, recommendation rails, carousels,
 * search results, etc.) into the canonical `Badge[]` array.
 *
 * WHY THIS EXISTS
 * ---------------
 * Per docs/Page-Types-And-Templates.md, every surface must render the SAME badge
 * set for a given product. The server-computed `badges: Badge[]` field (spec §6)
 * is the eventual source of truth, but until those API routes land, sitewide
 * widgets each hand-rolled their own badge logic (a red "🔥 Price Drop" string
 * here, a `CollectionBadge type="featured"` there) with divergent rules. That is
 * exactly the bug this task fixes.
 *
 * This adapter funnels all of those surfaces through the ONE canonical engine
 * (`determineBadges` in config.ts) + `badgesFromTypes`, so a product that
 * qualifies for "Price Drop" shows it in the sidebar, the bestsellers rail, the
 * recently-viewed rail, and search results — identically.
 *
 * UNITS: surfaces disagree on price units (RecommendedProduct uses dollars;
 * search/API rows use cents). To avoid unit bugs, the core `badgesForSignals`
 * takes already-normalized CENTS, and the `badgesFromRecommended` adapter does
 * the dollars→cents conversion in one place.
 *
 * When the server `badges` field ships, callers should prefer it directly and
 * this adapter becomes the fallback for surfaces that only have raw fields.
 */

import type {ListedOfferEvidence} from '@/lib/badges/price-evidence'
import { determineBadges } from './config'
import { badgesFromTypes, withSeenBefore, type Badge } from './select'

export interface ProductBadgeSignals {
  /** Explicit matching retailer offer; legacy aggregate originalPrice is not evidence. */
  listedOffer?:ListedOfferEvidence

  /** Current best price, in cents. */
  priceCents?: number
  /** Legacy aggregate original price; ignored for badge evidence. */
  originalPriceCents?: number
  /** Legacy 30-day aggregate; ignored without verified history. */
  avgPrice30dCents?: number
  /** Legacy historical minimum; ignored without verified history. */
  lowestPriceEverCents?: number
  rating?: number
  reviews?: number
  energyStar?: boolean
  /** ISO date string; drives the `new` badge (≤30 days). */
  createdAt?: string
  availabilityMaxQty?: number
  /** Retailer deal marker; presence drives the `deal` badge. */
  dealBadge?: string
  /** PostHog click count; drives `best_seller` (>100). */
  clickCount?: number
  /**
   * Client-only overlay: the visitor has this product in their recently-viewed
   * store. Prepends a `seen_before` badge (spec §4.3) — the ONLY client-side
   * mutation permitted.
   */
  seenBefore?: boolean
}

/**
 * Canonical badge resolution for any surface, from normalized signals.
 * Single code path → identical output for identical input, everywhere.
 */
export function badgesForSignals(signals: ProductBadgeSignals): Badge[] {
  const types = determineBadges({
    lowest_price_cents: signals.priceCents,
    average_price_30d_cents: signals.avgPrice30dCents,
    lowest_price_ever_cents: signals.lowestPriceEverCents,
    created_at: signals.createdAt,
    specs_json: {
      rating: signals.rating,
      reviews: signals.reviews,
      energy_star: signals.energyStar,
    },
    availabilityMaxQty: signals.availabilityMaxQty,
    dealBadge: signals.dealBadge,
    clickCount: signals.clickCount,
    offers: signals.listedOffer ? [{priceCents:signals.listedOffer.priceCents,originalPriceCents:signals.listedOffer.originalPriceCents,priceStale:signals.listedOffer.priceStale,isPriceUnknown:signals.listedOffer.isPriceUnknown,inStock:signals.listedOffer.inStock,status:signals.listedOffer.status}] : undefined,
  })

  const server = badgesFromTypes(types)
  // seen_before is layered on client-side AFTER the canonical set, then re-sorted.
  return withSeenBefore(server, signals.seenBefore === true)
}

/**
 * Adapter for the `RecommendedProduct`-ish shape used by most sidebar/sitewide
 * widgets (lib/recommendations.ts). Prices here are in DOLLARS, so we convert to
 * cents before handing off to the canonical engine.
 */
export function badgesFromRecommended(
  product: {
    price?: number
    listedOffer?:ListedOfferEvidence
    originalPrice?: number
    rating?: number
    reviews?: number
    created_at?: string
    specs_json?: {
      rating?: number
      reviews?: number
      energy_star?: boolean
      [key: string]: any
    } | null
  } | null | undefined,
  opts?: { seenBefore?: boolean }
): Badge[] {
  if (!product) return []
  const toCents = (v?: number) =>
    typeof v === 'number' && v > 0 ? Math.round(v * 100) : undefined

  return badgesForSignals({
    priceCents: toCents(product.price),
    listedOffer: product.listedOffer,
    originalPriceCents: toCents(product.originalPrice),
    rating: product.rating ?? product.specs_json?.rating,
    reviews: product.reviews ?? product.specs_json?.reviews,
    energyStar: product.specs_json?.energy_star === true,
    createdAt: product.created_at,
    seenBefore: opts?.seenBefore,
  })
}
